<template>
  <div
    :class="$style.base"
    ref="containerEl"
  >
    <Transition
      :name="transitionName"
      @after-enter="onAfterEnter"
      @after-leave="onAnimationEnd"
    >
      <div
        :key="step"
        :class="$style.slide"
        ref="slideEl"
      >
        <slot :name="String(step)" />
      </div>
    </Transition>
  </div>
</template>

<script
  setup
  lang="ts"
>
import { computed, ref, watch } from 'vue';

const emit = defineEmits(['animationEnd']);
const props = defineProps<{ step: number }>();

const last = ref(props.step);
const direction = ref<'fwd' | 'back'>('fwd');

watch(
  () => props.step,
  (n, o) => {
    const prev = o ?? last.value;
    direction.value = n >= prev ? 'fwd' : 'back';
    last.value = n;
    nextTick(() => {
      animateHeight();
    });
  },
);

const transitionName = computed(() =>
  direction.value === 'fwd' ? 'slide-left' : 'slide-right',
);

const slideEl = ref<HTMLElement | null>(null);
const containerEl = ref<HTMLElement | null>(null);

const animateHeight = () => {
  if (containerEl.value && slideEl.value) {
    containerEl.value.style.height = `${containerEl.value.offsetHeight}px`;
    nextTick(() => {
      if (containerEl.value && slideEl.value) {
        containerEl.value.style.height = `${slideEl.value.offsetHeight}px`;
      }
    });
  }
};

const onAfterEnter = () => {
  onAnimationEnd();
  nextTick(() => {
    emit('animationEnd');
  });
};

const onAnimationEnd = () => {
  if (containerEl.value) {
    containerEl.value.style.removeProperty('height');
  }
};
</script>

<style
  lang="scss"
  module
>
.base {
  position: relative;
  overflow: hidden;
  transition: height 300ms ease;
}

.slide {
  width: 100%;

  &:global(.slide-left-enter-active),
  &:global(.slide-left-leave-active),
  &:global(.slide-right-enter-active),
  &:global(.slide-right-leave-active) {
    transition: transform 300ms ease, opacity 300ms ease;
  }

  &:global(.slide-left-enter-from),
  &:global(.slide-right-leave-to) {
    transform: translateX(100%);
    opacity: 0;
  }
  &:global(.slide-left-leave-to),
  &:global(.slide-right-enter-from) {
    transform: translateX(-100%);
    opacity: 0;
  }

  &:global(.slide-left-enter-active),
  &:global(.slide-right-enter-active) {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
  }
}
</style>
